import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { Map } from "ol";
import { Fill, RegularShape, Stroke, Style, Text } from "ol/style";
import { Draw } from "ol/interaction";
import CircleStyle from "ol/style/Circle";
import {getArea, getLength} from 'ol/sphere.js';
import { LineString, Point, Polygon, Circle } from "ol/geom";
import { Coordinate } from "ol/coordinate";
import { EventEmitter } from "@angular/core";
import { circular } from "ol/geom/Polygon";

export enum MEASURE_MODE { line= 'LineString', area= 'Polygon', circle= 'Circle' };
    
export class MeasureLayer{
    
    private readonly source :VectorSource;
    private readonly vectorLayer : VectorLayer<VectorSource>;
    private readonly map: Map;
    private draw: Draw;
    private drawInteraction: Draw; 

    private measureMode : MEASURE_MODE = null;
    private isMeasuring = false;

    private drawPolygonCoordinates:Coordinate[][] = [];
    public drawPolygonCoordinatesEvent: EventEmitter<Coordinate[][]> = new EventEmitter();
    public drawCircleDataEvent: EventEmitter<{center: Coordinate, radius: number}> = new EventEmitter();

    

    public constructor(map: Map,  zIndex:number){
        this.map = map;
        this.source = new VectorSource();
       
        this.vectorLayer = new VectorLayer({
            source: this.source,
            style:(feature) => this.styleFunction(feature, true)
        });
        
       
        this.map.addLayer(this.vectorLayer);
        this.vectorLayer.setZIndex(zIndex);
    }

    public toggle(measureMode: MEASURE_MODE){ 
      if(this.isMeasuring && this.measureMode === measureMode){
        return;
      }
      
      if(this.isMeasuring){
        this.stop();
      }
      
      this.measureMode = measureMode;
      this.start();
    }

    public stop(){
      this.isMeasuring = false;
      this.measureMode = null;
      if (this.draw) {
        this.map.removeInteraction(this.draw);
        this.draw = null;
      }
        this.source.clear();
    }

    public clearDrawings(): void {
      this.map.removeInteraction(this.drawInteraction);
      // Ajouter aussi le clear de la source ici
      this.source.clear();  
}

    private start(){
      this.isMeasuring = true;
      
      if (this.measureMode === MEASURE_MODE.circle) {
        this.draw = new Draw({
          source: this.source,
          type: 'Circle',
          style: (feature) => {
            return this.styleFunction(feature, false, 'Circle');
          }
        });
      } else {
        this.draw = new Draw({
          source: this.source,
          type: this.measureMode,
          style: (feature) => {
            return this.styleFunction(feature, true, this.measureMode);
          }
        });
      }
    
      this.draw.on('drawstart', (evt) => {
        console.log('drawstart');
        this.source.clear();
        
        if (this.measureMode === MEASURE_MODE.circle) {
          const sketch = evt.feature;
          
          sketch.getGeometry().on('change', (evt) => {
            const geom = evt.target;
            if (geom instanceof Circle) {
              const radius = geom.getRadius();
              console.log('Radius en temps réel:', radius);
            }
          });
        }
      });
    
      this.draw.on('drawend', (evt) => {
        console.log('drawend triggered');
        const feature = evt.feature;
        const geometry = feature.getGeometry();
        
        if (this.measureMode === MEASURE_MODE.circle) {
          const circle = geometry as Circle;
          console.log('Circle drawn with center:', circle.getCenter(), 'radius:', circle.getRadius());
          
          this.drawCircleDataEvent.emit({ center: circle.getCenter(), radius: circle.getRadius() });
          const circularPolygon = circular(circle.getCenter(), circle.getRadius(), 64);
          this.drawPolygonCoordinates = circularPolygon.getCoordinates();
          this.drawPolygonCoordinatesEvent.emit(this.drawPolygonCoordinates);
        } else if (this.measureMode === MEASURE_MODE.area) {
          const polygon = geometry as Polygon;
          this.drawPolygonCoordinates = polygon.getCoordinates();
          this.drawPolygonCoordinatesEvent.emit(this.drawPolygonCoordinates);
        }
        
        console.log('Drawing finished, ready for next drawing');
      });
    
      this.map.addInteraction(this.draw);
      this.drawInteraction = this.draw; // Exposer l'interaction de dessin
    }

     public getDrawInteraction(): Draw {
    return this.drawInteraction;
    }
    private styleFunction(feature, segments, drawType?) {
  const styles = [];
  const geometry = feature.getGeometry();
  const type = geometry.getType();
  
  if (!this.shouldApplyStyle(type, drawType)) {
    return styles;
  }
  
  if (type === 'Circle') {
    return this.createCircleStyles(geometry, styles);
  }
  
  if (type === 'Polygon') {
    return this.createPolygonStyles(geometry, segments, styles);
  }
  
  if (type === 'LineString') {
    return this.createLineStringStyles(geometry, segments, styles);
  }
  
  return styles;
}

private shouldApplyStyle(type: string, drawType?: string): boolean {
  return !drawType || drawType === type || type === 'Point';
}

private createCircleStyles(geometry: any, styles: any[]): any[] {
  styles.push(circleStyle);
  
  const circle = geometry as Circle;
  const center = circle.getCenter();
  const radius = circle.getRadius();
  
  if (radius <= 0) {
    return styles;
  }
  
  // Ajouter la ligne de rayon
  this.addRadiusLine(center, radius, styles);
  
  // Ajouter les labels
  this.addCircleLabels(center, radius, styles);
  
  return styles;
}

private addRadiusLine(center: Coordinate, radius: number, styles: any[]): void {
  const radiusEndPoint = [center[0] + radius, center[1]];
  const radiusLine = new LineString([center, radiusEndPoint]);
  
  const radiusLineStyleClone = radiusLineStyle.clone();
  radiusLineStyleClone.setGeometry(radiusLine);
  styles.push(radiusLineStyleClone);
  
  const radiusLabel = formatLength(radiusLine);
  const radiusPoint = new Point(radiusLine.getCoordinateAt(0.5));
  const radiusLabelStyle = labelStyle.clone();
  radiusLabelStyle.setGeometry(radiusPoint);
  radiusLabelStyle.getText().setText('R: ' + radiusLabel);
  styles.push(radiusLabelStyle);
}

private addCircleLabels(center: Coordinate, radius: number, styles: any[]): void {
  const radiusEndPoint = [center[0] + radius, center[1]];
  const radiusLine = new LineString([center, radiusEndPoint]);
  const geodesicRadius = getLength(radiusLine);
  
  const areaLabel = formatCircleAreaWithGeodesicRadius(geodesicRadius);
  const areaPoint = new Point(center);
  const areaLabelStyle = labelStyle.clone();
  areaLabelStyle.setGeometry(areaPoint);
  areaLabelStyle.getText().setText(areaLabel);
  areaLabelStyle.getText().setOffsetY(-35);
  styles.push(areaLabelStyle);
}

private createPolygonStyles(geometry: any, segments: boolean, styles: any[]): any[] {
  styles.push(style);
  
  const point = geometry.getInteriorPoint();
  const label = formatArea(geometry);
  const line = new LineString(geometry.getCoordinates()[0]);
  
  this.addLabelIfValid(point, label, styles);
  this.addSegmentStylesIfNeeded(segments, line, 'Polygon', styles);
  
  return styles;
}

private createLineStringStyles(geometry: any, segments: boolean, styles: any[]): any[] {
  styles.push(style);
  
  const point = new Point(geometry.getLastCoordinate());
  const label = formatLength(geometry);
  const line = geometry;
  
  this.addLabelIfValid(point, label, styles);
  this.addSegmentStylesIfNeeded(segments, line, 'LineString', styles);
  
  return styles;
}

private addLabelIfValid(point: any, label: string, styles: any[]): void {
  if (label && point) {
    const labelStyleClone = labelStyle.clone();
    labelStyleClone.setGeometry(point);
    labelStyleClone.getText().setText(label);
    styles.push(labelStyleClone);
  }
}

private addSegmentStylesIfNeeded(segments: boolean, line: any, type: string, styles: any[]): void {
  if (!segments || !line || type === 'Circle') {
    return;
  }
  
  const segmentStyles = [];
  let count = 0;
  
  line.forEachSegment((a, b) => {
    const segment = new LineString([a, b]);
    const segmentLabel = formatLength(segment);
    
    if (segmentStyles.length - 1 < count) {
      segmentStyles.push(segmentStyle.clone());
    }
    
    const segmentPoint = new Point(segment.getCoordinateAt(0.5));
    segmentStyles[count].setGeometry(segmentPoint);
    segmentStyles[count].getText().setText(segmentLabel);
    styles.push(segmentStyles[count]);
    count++;
  });
}
    
}

const style = new Style({
  fill: new Fill({
    color: 'rgba(100, 100, 255, 0.4)',
  }),
  stroke: new Stroke({
    color: 'rgba(0, 153, 255, 1)',
    width: 3,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)',
    }),
  }),
});

const segmentStyle = new Style({
  text: new Text({
      font: '12px Calibri,sans-serif',
      fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
      }),
      backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)',
      }),
      padding: [2, 2, 2, 2],
      textBaseline: 'bottom',
      offsetY: -12,
  }),
  image: new RegularShape({
      radius: 6,
      points: 3,
      angle: Math.PI,
      displacement: [0, 8],
      fill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)',
      }),
  }),
});

const labelStyle = new Style({
  text: new Text({
      font: '14px Calibri,sans-serif',
      fill: new Fill({
          color: 'rgba(255, 255, 255, 1)',
      }),
      backgroundFill: new Fill({
          color: 'rgba(0, 0, 0, 0.7)',
      }),
      padding: [3, 3, 3, 3],
      textBaseline: 'bottom',
      offsetY: -15,
  }),
  image: new RegularShape({
      radius: 8,
      points: 3,
      angle: Math.PI,
      displacement: [0, 10],
      fill: new Fill({
          color: 'rgba(0, 0, 0, 0.7)',
      }),
  }),
});

const circleStyle = new Style({
  fill: new Fill({
    color: 'rgba(255, 100, 100, 0.3)', 
  }),
  stroke: new Stroke({
    color: 'rgba(255, 0, 0, 1)', 
    lineDash: [5, 5], 
    width: 3,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(255, 0, 0, 0.8)',
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)',
    }),
  }),
});

const radiusLineStyle = new Style({
  stroke: new Stroke({
    color: 'rgba(255, 0, 0, 0.8)', 
    width: 2,
    lineDash: [2, 4], 
  }),
});

const formatLength = function (line) {
  const length = getLength(line);
  let output;
  if (length > 1000) {
    output = Math.round((length / 1000) * 100) / 100 + ' km';
  } else {
    output = Math.round(length * 100) / 100 + ' m';
  }
  return output;
};

const formatArea = function (polygon) {
  const area = getArea(polygon);
  let output;
  if (area > 1000000) {
    output = Math.round((area / 1000000) * 100) / 100 + ' km\xB2';
  } else {
    output = Math.round(area * 100) / 100 + ' m\xB2';
  }
  console.log(output);
  return output;
};

// Nouvelle fonction pour calculer l'aire avec le rayon géodésique
const formatCircleAreaWithGeodesicRadius = function (geodesicRadius) {
  const area = Math.PI * geodesicRadius * geodesicRadius;
  let output;
  if (area > 1000000) {
    output = Math.round((area / 1000000) * 100) / 100 + ' km²';
  } else {
    output = Math.round(area * 100) / 100 + ' m²';
  }
  console.log('Circle area:', output);
  return output;
};

// Anciennes fonctions commentées pour maintenir la cohérence
// const formatCircleArea = function (radius) {
//   const area = Math.PI * radius * radius;
//   let output;
//   if (area > 1000000) {
//     output = Math.round((area / 1000000) * 100) / 100 + ' km²';
//   } else {
//     output = Math.round(area * 100) / 100 + ' m²';
//   }
//   console.log('Circle area:', output);
//   return output;
// };

// const formatCircleRadius = function (radius) {
//   let output;
//   if (radius > 1000) {
//     output = Math.round((radius / 1000) * 100) / 100 + ' km';
//   } else {
//     output = Math.round(radius * 100) / 100 + ' m';
//   }
//   return 'R: ' + output;
// };